import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from 'react';
import { cva } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/cn.js';
import { FOCUS_RING } from '../../lib/focus-ring.js';
/**
 * Button (README.md > Atoms > Button). Commits an action. Convention carried
 * by the caller, not enforced here: one primary per immediate context — the
 * strongest action of a region, not of the whole page (DESIGN.md, §7).
 */
/**
 * Exported because a navigation control is an anchor, not a button, and an
 * anchor that looks like a button should be the same anchor everywhere. Used
 * by the app for router links; the component below uses it too.
 */
export const buttonClasses = cva(cn('inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm', 'text-button font-semibold tracking-[-0.005em]', 'transition-colors duration-fast disabled:pointer-events-none', 
// A loading button is `aria-disabled` rather than `disabled` (see
// `Button` below), and looks exactly as a disabled one always has.
'aria-disabled:pointer-events-none', FOCUS_RING), {
    variants: {
        variant: {
            // Primary and ghost carry a transparent border where the design shows
            // none. Forced-colours mode drops fills and paints borders, so without
            // one they were bare words there — "Next" and "Save & close" read as
            // text, not as things to press. The same width as the outlined
            // variants, so no two variants differ in size. In the variants, not
            // the base, so no class string ever holds two border colours.
            primary: cn('border-[1.5px] border-transparent bg-primary text-text-inverse', 'hover:bg-primary-hover', 'disabled:bg-border disabled:text-text-disabled', 'aria-disabled:bg-border aria-disabled:text-text-disabled'),
            secondary: cn('bg-surface text-text border-[1.5px] border-border-strong', 'hover:bg-surface-secondary hover:border-text-disabled', 'disabled:text-text-disabled disabled:border-border-strong', 'aria-disabled:text-text-disabled aria-disabled:border-border-strong'),
            ghost: cn('border-[1.5px] border-transparent bg-transparent text-primary', 'hover:bg-primary-light hover:text-primary-dark', 'disabled:text-text-disabled', 'aria-disabled:text-text-disabled'),
            destructive: cn('bg-surface text-error-text border-[1.5px] border-error-border', 'hover:bg-error-light', 'disabled:text-text-disabled disabled:border-border-strong', 'aria-disabled:text-text-disabled aria-disabled:border-border-strong'),
        },
        size: {
            sm: 'h-control-sm px-4',
            md: 'h-control-md px-5',
            lg: 'h-control-lg px-6',
        },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
});
/**
 * A loading button stays focusable. It is `aria-disabled` and ignores
 * activation, rather than `disabled`: a browser takes focus away from a
 * control the moment it becomes disabled and hands it to the page, so a
 * keyboard user who pressed Save found themselves back at the top of the
 * document with no idea where they had been. Now focus stays on the action
 * that is running, and a second press does nothing while it runs. `disabled`
 * still means disabled.
 *
 * With `loadingText`, both labels are laid out in the same cell and only one
 * is shown, so the button is as wide as the wider of the two and does not
 * change size when it starts or stops loading (DESIGN.md: "keeps its width
 * to avoid shifting the layout"). The hidden one is `aria-hidden`, so the
 * button's name is always the label on screen.
 */
export const Button = forwardRef(function Button({ className, variant, size, loading = false, loadingText, disabled, onClick, children, ...props }, ref) {
    const spinner = _jsx(Loader2, { className: "size-icon-sm animate-spin", "aria-hidden": "true" });
    return (_jsx("button", { ref: ref, type: props.type ?? 'button', className: cn(buttonClasses({ variant, size }), className), disabled: disabled, "aria-disabled": (loading && !disabled) || undefined, "aria-busy": loading || undefined, ...props, onClick: (event) => {
            // Cancelling the click also stops a submit button submitting its
            // form, including by Enter in one of the form's fields.
            if (loading) {
                event.preventDefault();
                return;
            }
            onClick?.(event);
        }, children: loadingText === undefined ? (_jsxs(_Fragment, { children: [loading ? spinner : null, children] })) : (_jsxs("span", { className: "grid", children: [_jsx("span", { "aria-hidden": loading || undefined, className: cn('col-start-1 row-start-1 inline-flex items-center justify-center gap-2', loading && 'invisible'), children: children }), _jsxs("span", { "aria-hidden": !loading || undefined, className: cn('col-start-1 row-start-1 inline-flex items-center justify-center gap-2', !loading && 'invisible'), children: [spinner, loadingText] })] })) }));
});
//# sourceMappingURL=Button.js.map